import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useKyc } from "../../context/KycContext";
import AccountInfoCard from "../../components/kyc/AccountInfoCard";
import { getProfile } from "../../services/authService";

function statusColor(s) {
  if (s === "verified" || s === "approved") return "bg-emerald-100 text-emerald-700";
  if (s === "rejected" || s === "re_kyc") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

export default function KycPage() {
  const navigate = useNavigate();
  const { kycStatus, loading, error, refreshKycStatus, isVerified } = useKyc();
  const [user, setUser] = useState(null);

  useEffect(() => {
    refreshKycStatus();
    getProfile().then((res) => {
      setUser(res.data?.data || res.data);
    }).catch(() => {});
  }, []);

  // Redirect to dashboard if verified
  useEffect(() => {
    if (isVerified) navigate("/dashboard", { replace: true });
  }, [isVerified]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-purple-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isSubmitted = kycStatus?.kyc_submitted && kycStatus?.documents_submitted;

  return (
        <div className="flex-1 p-8 overflow-y-auto">

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">{error}</div>
          )}

          {/* Status Card */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-bold text-[#34094D]">KYC Status</h1>
              {kycStatus?.kyc_status && (
                <span className={`px-3 py-1.5 rounded-full text-[13px] font-bold uppercase ${statusColor(kycStatus.kyc_status)}`}>
                  {kycStatus.kyc_status.replace("_", " ")}
                </span>
              )}
            </div>

            {/* Not submitted yet — either condition false */}
            {!isSubmitted && (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">Your KYC is incomplete. Please complete all steps to proceed.</p>
                <button
                  onClick={() => navigate("/onboard")}
                  className="rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white px-5 py-3 font-bold hover:opacity-90 transition cursor-pointer border-none"
                >
                  Complete Your KYC
                </button>
              </div>
            )}

            {/* Submitted — show status info */}
            {isSubmitted && (
              <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[11px] uppercase text-gray-400 tracking-wider mb-1">KYC Submitted</p>
                  <p className="font-bold text-[#34094D]">{kycStatus.kyc_submitted ? "Yes" : "No"}</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[11px] uppercase text-gray-400 tracking-wider mb-1">Documents</p>
                  <p className="font-bold text-[#34094D]">{kycStatus.documents_submitted ? "Submitted" : "Pending"}</p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[11px] uppercase text-gray-400 tracking-wider mb-1">Status</p>
                  <p className={`font-bold capitalize ${statusColor(kycStatus.kyc_status).split(" ")[1]}`}>
                    {kycStatus.kyc_status?.replace("_", " ") || "—"}
                  </p>
                </div>
              </div>

                {/* Rejected — show Re-submit button */}
                {kycStatus.kyc_status === "rejected" || kycStatus.kyc_status === "re_kyc" ? (
                  <button
                    onClick={() => navigate("/onboard")}
                    className="rounded-lg bg-gradient-to-r from-red-500 to-red-700 text-white px-5 py-3 font-bold hover:opacity-90 transition cursor-pointer border-none"
                  >
                    Re-submit KYC
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {/* Account Info + Documents — always shown when KYC data is available */}
          {kycStatus && (
            <AccountInfoCard kyc={kycStatus} user={user} />
          )}

        </div>
  );
}
